import React, {useRef, useState} from 'react';
import {
  Editable,
  EditableInput,
  Input,
  useTheme,
  Flex,
  Box,
  Select,
  VStack,
  Text,
  IconButton,
} from '@chakra-ui/react';
import {TextG10, TextG12} from '../../../components/typography';
import EditableControls from '../../../components/EditableControls';
import {useUpdateMeetUp} from '../../../hooks/useMeetUps';
import {getEventDate} from '../../../utils/date';
import dayjs from 'dayjs';
import Icon from '../../../components/Icon';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';

const DateTimeEditor: React.FC<{event: any}> = ({event}) => {
  const theme = useTheme();
  const {mutateAsync} = useUpdateMeetUp();
  const inputRef = useRef<HTMLInputElement>(null);
  // Friendly display date (could be something like "Friday, Oct 1, 2:00 PM")
  const eventDate = getEventDate(event.start_date_time, event.end_date_time);

  // State to hold the datetime value (always in ISO format)
  const [dateValue, setDateValue] = useState(
    dayjs.utc(event.start_date_time).local().format(DATE_FORMAT)
  );

  // Calculate initial duration from start and end times (in hours)
  const initialDuration = Math.round(
    dayjs.utc(event.end_date_time).diff(dayjs.utc(event.start_date_time), 'hour')
  );

  // State to hold the duration value
  const [durationValue, setDurationValue] = useState(initialDuration || 3);

  // Track if user is editing
  const [isEditingDate, setIsEditingDate] = useState(false);

  const handleCancel = () => {
    setIsEditingDate(false);
    // Reset to original values
    setDateValue(dayjs.utc(event.start_date_time).local().format(DATE_FORMAT));
    setDurationValue(initialDuration || 3);
  };

  return (
    <Editable
      pos="relative"
      w="100%"
      fontSize="10px"
      fontFamily="Gordita"
      value={dateValue}
      onChange={setDateValue}
      onEdit={() => {
        inputRef.current?.focus();
        setIsEditingDate(true);
      }}
      onCancel={handleCancel}
      onSubmit={(newValue) => {
        const newStart = dayjs(newValue);
        const newEnd = newStart.add(durationValue, 'hour');
        const utcStartDate = newStart.utc().format(DATE_FORMAT);
        const utcEndDate = newEnd.utc().format(DATE_FORMAT);

        mutateAsync({
          ...event,
          start_date_time: utcStartDate,
          end_date_time: utcEndDate,
          eventId: event.id,
        });
        setIsEditingDate(false);
      }}
      isPreviewFocusable={false}
      selectAllOnFocus={false}>
      {isEditingDate ? (
        <VStack spacing={3} align="stretch" w="100%">
          <Input
            ref={inputRef}
            as={Input}
            type="datetime-local"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            borderRadius="md"
            fontSize="14px"
            height="44px"
            px={3}
            mt={1}
            sx={{
              '-webkit-appearance': 'none',
              appearance: 'none',
              touchAction: 'manipulation',
              fontSize: '16px',
            }}
          />

          <Flex alignItems="center" gap="10px" mt={2} w="100%">
            <TextG12 color={theme.colors.lightBlack} flexShrink={0}>
              Duration:
            </TextG12>
            <Select
              value={durationValue}
              onChange={(e) => setDurationValue(Number(e.target.value))}
              fontSize="16px"
              height="44px"
              borderRadius="md"
              flex="1"
              // sx={{
              //   '-webkit-appearance': 'menulist',
              //   touchAction: 'manipulation',
              // }}
            >
              {Array.from({length: 24}, (_, i) => i + 1).map((num) => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'hour' : 'hours'}
                </option>
              ))}
            </Select>
          </Flex>
        </VStack>
      ) : (
        <TextG10 fontWeight="500" color={theme.colors.grey} maxW="150px" isTruncated noOfLines={1}>
          {eventDate}
        </TextG10>
      )}
      <EditableControls />
    </Editable>
  );
};

export default DateTimeEditor;
